import axios from "axios";
import { getApiBase } from "./apiConfig";
import { apiClient } from "./apiClient";
import { logger } from "./logger";
import type {
  TradingSystemPayload,
  TradingSettings,
  TradingMode,
  ExecutedTrade,
  TradesFilterResponse,
  PendingOrdersResponse,
  ManualApprovalQueueResponse,
} from "@shared/types";

const API_BASE = getApiBase();
const TRADING_SYSTEM_BASE = `${API_BASE}/api/dashboard/trading-system`;

/**
 * Get circuit breaker status and metrics
 */
export async function getCircuitBreakerStatus(): Promise<any> {
  try {
    // Use apiClient instead of axios to ensure credentials are included
    // apiClient uses baseURL=/api, so use relative path
    const response = await apiClient.get("/dashboard/trading-system/execution/circuit-breaker/status");
    return response.data;
  } catch (error) {
    logger.error("Error getting circuit breaker status:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

/**
 * Manually pause execution (circuit breaker)
 */
export async function manualPauseExecution(request: { reason?: string }): Promise<any> {
  try {
    // Use apiClient instead of axios to ensure CSRF token is included
    // apiClient uses baseURL=/api, so use relative path
    const response = await apiClient.post("/dashboard/trading-system/execution/circuit-breaker/pause", request);
    return response.data;
  } catch (error) {
    logger.error("Error pausing execution:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

/**
 * Manually resume execution (circuit breaker)
 */
export async function manualResumeExecution(request: { reason?: string }): Promise<any> {
  try {
    // Use apiClient instead of axios to ensure CSRF token is included
    // apiClient uses baseURL=/api, so use relative path
    const response = await apiClient.post("/dashboard/trading-system/execution/circuit-breaker/resume", request);
    return response.data;
  } catch (error) {
    logger.error("Error resuming execution:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export function getWebSocketUrl(): string {
  try {
    const apiBase = getApiBase();

    if (apiBase && apiBase.length > 0 && typeof apiBase === "string") {
      const wsProtocol = apiBase.startsWith("https") ? "wss" : "ws";
      let wsHost = apiBase.replace(/^https?:\/\//, "").replace(/\/api$/, "");
      if (wsHost.length === 0) {
        wsHost = "localhost:8000";
      }
      const wsUrl = `${wsProtocol}://${wsHost}/api/dashboard/trading-system/ws`;
      if (wsUrl.startsWith("ws://") || wsUrl.startsWith("wss://")) {
        return wsUrl;
      }
    }

    // Same-origin deployment (empty apiBase): construct WebSocket URL from current page location
    if (typeof window !== "undefined" && window.location) {
      const wsProtocol = window.location.protocol === "https:" ? "wss" : "ws";
      const wsUrl = `${wsProtocol}://${window.location.host}/api/dashboard/trading-system/ws`;
      return wsUrl;
    }

    return "ws://localhost:8000/api/dashboard/trading-system/ws";
  } catch (error) {
    logger.error(
      "Error constructing WebSocket URL",
      error instanceof Error ? error : new Error(String(error)),
    );
    return "ws://localhost:8000/api/dashboard/trading-system/ws";
  }
}

export interface UpdateTradingModeRequest {
  mode: TradingMode;
}

export interface CustomPairsRequest {
  pairs: string[];
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    let statusCode = response.status;
    try {
      const payload = await response.json();
      if (typeof payload?.detail === "string") {
        message = payload.detail;
      } else if (payload?.detail && typeof payload.detail === "object") {
        // Handle structured error response (e.g., 410 Gone with metadata)
        message = payload.detail.message || message;
        statusCode = response.status;
      }
    } catch (error) {
      // ignore json parse errors
    }
    const error = new Error(message) as Error & { statusCode?: number };
    error.statusCode = statusCode;
    throw error;
  }
  if (response.status === 204) {
    return undefined as T;
  }
  try {
    return (await response.json()) as T;
  } catch (error) {
    throw new Error("Failed to parse response as JSON");
  }
}

export async function getTradingSystemPayload(): Promise<TradingSystemPayload> {
  try {
    // Use apiClient instead of fetch to handle CORS, CSP, and proxy properly
    // Backend timeout is 300s (5 minutes), so we set a longer timeout here
    // Backend returns cached payload immediately (< 1s) if available
    // If no cache, backend generates fresh payload (may take up to 300s)
    const response = await apiClient.get<TradingSystemPayload>("/dashboard/trading-system/", {
      timeout: 320000, // 320 seconds (5 minutes + 20s buffer) to match backend timeout
    });
    return response.data;
  } catch (error: any) {
    // Handle timeout errors
    if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
      throw new Error("Request timeout. The server is taking too long to respond.");
    }
    // Handle network errors
    if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
      throw new Error("Network error. Please check your connection and ensure the backend server is running.");
    }
    // Re-throw other errors
    throw error;
  }
}

export async function updateDataTradingMode(
  request: UpdateTradingModeRequest
): Promise<{ status: string; message: string }> {
  // Use apiClient instead of fetch to ensure credentials and CSRF token are included.
  // apiClient uses baseURL=/api, so use the relative path (no /api prefix).
  const response = await apiClient.post<{ status: string; message: string }>(
    "/dashboard/trading-system/modes/data",
    request
  );
  return response.data;
}

export async function updateCustomPairs(
  request: CustomPairsRequest
): Promise<{ status: string; message: string; pair_selection_mode: string }> {
  // Use apiClient instead of fetch to ensure credentials and CSRF token are included.
  const response = await apiClient.post<{
    status: string;
    message: string;
    pair_selection_mode: string;
  }>("/dashboard/trading-system/custom-pairs", request);
  return response.data;
}

export interface InjectTradeRequest {
  symbol: string;
  side: "buy" | "sell";
  entry_price?: number;
  stop_loss?: number;
  take_profit?: number[];
  confidence?: number;
  source?: string;
  summary?: string;
  risk_reward_ratio?: number;
  entry_min?: number;
  entry_max?: number;
  direction?: string;
}

export interface InjectTradeResponse {
  signal_id: string;
  recommendation_id: string;
  status: string;
  message: string;
}

export async function injectTrade(
  request: InjectTradeRequest
): Promise<InjectTradeResponse> {
  try {
    // Use apiClient instead of fetch to ensure credentials and CSRF token are included
    // apiClient uses baseURL=/api, so use relative path
    const response = await apiClient.post("/dashboard/trading-system/inject-trade", request);
    return response.data;
  } catch (error: any) {
    logger.error("Error injecting trade:", error);
    
    // Extract error message from response
    if (error.response?.data?.detail) {
      const errorMessage = typeof error.response.data.detail === "string" 
        ? error.response.data.detail 
        : JSON.stringify(error.response.data.detail);
      throw new Error(errorMessage);
    }
    
    // Fallback to generic error message
    throw new Error(error.message || "Failed to inject trade. Please check your input and try again.");
  }
}

export interface TradesFilterRequest {
  source?: string;
  status?: string;
  symbol?: string;
  userId?: string;
  timeRange?: string;
  timeRangeHours?: number;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

export interface PositionControlRequest {
  action: "partial_exit" | "full_exit" | "adjust_stop" | "adjust_target";
  quantity?: number;
  stop_loss?: number;
  take_profit?: number[];
}

export async function controlPosition(
  positionId: string,
  request: PositionControlRequest
): Promise<{ status: string; message: string }> {
  try {
    // Use apiClient instead of fetch to ensure credentials and CSRF token are included
    // apiClient uses baseURL=/api, so use relative path
    // Note: position_id is in URL path, not in request body (matches backend API design)
    const response = await apiClient.post<{ status: string; message: string }>(
      `/dashboard/trading-system/positions/${encodeURIComponent(positionId)}/control`,
      request
    );
    return response.data;
  } catch (error) {
    logger.error("Error controlling position:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function getExecutedTrades(
  filters: TradesFilterRequest = {}
): Promise<TradesFilterResponse> {
  const params = new URLSearchParams();
  if (filters.source) {
    params.set("source", filters.source);
  }
  if (filters.status) {
    params.set("status", filters.status);
  }
  if (filters.symbol) {
    params.set("symbol", filters.symbol);
  }
  if (filters.userId) {
    params.set("user_id", filters.userId);
  }
  if (filters.timeRange) {
    params.set("time_range", filters.timeRange);
  }
  if (filters.timeRangeHours) {
    params.set("time_range_hours", String(filters.timeRangeHours));
  }
  if (filters.startDate) {
    params.set("start_date", filters.startDate);
  }
  if (filters.endDate) {
    params.set("end_date", filters.endDate);
  }
  if (filters.page) {
    params.set("page", String(filters.page));
  }
  if (filters.pageSize) {
    params.set("page_size", String(filters.pageSize));
  }

  const query = params.toString();
  const url = query ? `${TRADING_SYSTEM_BASE}/trades?${query}` : `${TRADING_SYSTEM_BASE}/trades`;
  const response = await fetch(url);
  return handleResponse<TradesFilterResponse>(response);
}

export interface ExecutionControlRequest {
  action: "start" | "pause";
}

export async function controlExecution(
  request: ExecutionControlRequest
): Promise<{ status: string; message: string }> {
  const response = await apiClient.post<{ status: string; message: string }>(
    "/dashboard/trading-system/execution/control",
    request
  );
  return response.data;
}

export async function getExecutionStatus(): Promise<{ status: string; message: string }> {
  const response = await apiClient.get<{ status: string; message: string }>(
    "/dashboard/trading-system/execution/status"
  );
  return response.data;
}

export async function getPendingOrders(): Promise<PendingOrdersResponse> {
  const response = await fetch(`${TRADING_SYSTEM_BASE}/orders/pending`);
  return handleResponse<PendingOrdersResponse>(response);
}

export interface TechnicalAnalysis {
  symbol: string;
  timeframe: string;
  timestamp: string;
  trend: {
    score: number;
    direction: string;
    ma_signals: Record<string, number>;
  };
  momentum: {
    score: number;
    rsi: number | null;
    macd: string | null;
    stochastic: number | null;
  };
  volatility: {
    score: number;
    atr: number | null;
    bollinger: string | null;
  };
  volume: {
    score: number;
  };
  overall: {
    score: number;
    signal_strength: string;
    confidence_level: string;
  };
  indicators: Array<{
    name: string;
    value: number;
    signal: string;
    strength: number;
  }>;
  patterns: Array<Record<string, unknown>>;
}

export async function getTechnicalAnalysis(
  symbol: string,
  timeframes?: string,
): Promise<TechnicalAnalysis> {
  const params = new URLSearchParams();
  if (timeframes) {
    params.append("timeframes", timeframes);
  }
  
  const url = `${TRADING_SYSTEM_BASE}/technical-analysis/${encodeURIComponent(symbol)}${params.toString() ? `?${params.toString()}` : ""}`;
  const response = await fetch(url);
  
  if (!response.ok) {
    throw new Error(`Failed to get technical analysis: ${response.statusText}`);
  }
  
  return response.json();
}

/** @deprecated Use approveTradeRecommendation instead */
export async function approveRecommendation(id: string): Promise<{ status: string; message: string }> {
  const response = await apiClient.post<{ status: string; message: string }>(
    `/dashboard/trading-system/recommendations/${id}/approve`
  );
  return response.data;
}

/** @deprecated Use rejectTradeRecommendation instead */
export async function rejectRecommendation(id: string): Promise<{ status: string; message: string }> {
  const response = await apiClient.post<{ status: string; message: string }>(
    `/dashboard/trading-system/recommendations/${id}/reject`
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Manual approval supervision queue (admin only)
// ---------------------------------------------------------------------------

export async function getSupervisionQueue(): Promise<ManualApprovalQueueResponse> {
  const response = await apiClient.get<ManualApprovalQueueResponse>(
    "/dashboard/trading-system/recommendations/supervision"
  );
  return response.data;
}

export async function approveManualRecommendation(
  id: string,
  reason?: string
): Promise<{ status: string; recommendation_id: string }> {
  const response = await apiClient.post<{ status: string; recommendation_id: string }>(
    `/dashboard/trading-system/recommendations/${encodeURIComponent(id)}/approve`,
    reason ? { reason } : {}
  );
  return response.data;
}

export async function rejectManualRecommendation(
  id: string,
  reason: string
): Promise<{ status: string; recommendation_id: string }> {
  const response = await apiClient.post<{ status: string; recommendation_id: string }>(
    `/dashboard/trading-system/recommendations/${encodeURIComponent(id)}/reject`,
    { reason }
  );
  return response.data;
}

export async function holdManualRecommendation(
  id: string,
  durationMinutes: number,
  reason: string
): Promise<{ status: string; recommendation_id: string }> {
  const response = await apiClient.post<{ status: string; recommendation_id: string }>(
    `/dashboard/trading-system/recommendations/${encodeURIComponent(id)}/hold`,
    { duration_minutes: durationMinutes, reason }
  );
  return response.data;
}

export async function forceApproveManualRecommendation(
  id: string,
  reason: string
): Promise<{ status: string; recommendation_id: string }> {
  const response = await apiClient.post<{ status: string; recommendation_id: string }>(
    `/dashboard/trading-system/recommendations/${encodeURIComponent(id)}/force-approve`,
    { reason }
  );
  return response.data;
}

export interface BulkManualActionResult {
  recommendation_id: string;
  success?: boolean;
  error?: string;
}

export async function bulkApproveManualRecommendations(
  recommendationIds: string[],
  reason?: string
): Promise<{ status: string; results: BulkManualActionResult[] }> {
  const response = await apiClient.post<{ status: string; results: BulkManualActionResult[] }>(
    "/dashboard/trading-system/recommendations/bulk-approve",
    { recommendation_ids: recommendationIds, ...(reason ? { reason } : {}) }
  );
  return response.data;
}

export async function bulkRejectManualRecommendations(
  recommendationIds: string[],
  reason?: string
): Promise<{ status: string; results: BulkManualActionResult[] }> {
  const response = await apiClient.post<{ status: string; results: BulkManualActionResult[] }>(
    "/dashboard/trading-system/recommendations/bulk-reject",
    { recommendation_ids: recommendationIds, ...(reason ? { reason } : {}) }
  );
  return response.data;
}

export async function getManualApprovalNotifications(): Promise<{ success: boolean; enabled: boolean }> {
  const response = await apiClient.get<{ success: boolean; enabled: boolean }>(
    "/dashboard/trading-system/manual-approval/notifications"
  );
  return response.data;
}

export async function setManualApprovalNotifications(
  enabled: boolean
): Promise<{ success: boolean; enabled: boolean }> {
  const response = await apiClient.post<{ success: boolean; enabled: boolean }>(
    "/dashboard/trading-system/manual-approval/notifications",
    { enabled }
  );
  return response.data;
}

export interface RecommendationsTechnicalAnalysis {
  analyses: Record<string, {
    symbol: string;
    overall_score: number;
    signal_strength: string;
    confidence_level: string;
    trend_direction: string;
    indicators_count: number;
  }>;
  total: number;
}

export interface AIDecision {
  action: string;
  confidence: number | null;
  reason: string;
  timestamp: string;
  price_before: number | null;
  price_after: number | null;
}

export interface TechnicalIndicators {
  rsi?: number;
  macd?: any;
  ema?: any;
  bollinger_bands?: any;
  technical_score?: number;
  volume?: any;
  price?: number;
  trend?: string;
  support_resistance?: any;
}

export interface PartialExit {
  timestamp: string;
  exit_percentage: number;
  exit_price: number;
  pnl_percentage: number;
  reason: string;
  tp_level: "TP1" | "TP2" | "TP3";
  closed_quantity: number;
}

export interface PositionAIDecisionsResponse {
  position_id: string;
  symbol: string;
  current_decision: AIDecision | null;
  technical_indicators: TechnicalIndicators | null;
  decision_history: Array<AIDecision & { id: string; technical_analysis?: any }>;
  total_decisions: number;
  partial_exits?: PartialExit[];
  automated_actions?: any[];  // Placeholder for future automated actions tracking
}

export async function getPositionAIDecisions(
  positionId: string,
  limit: number = 20
): Promise<PositionAIDecisionsResponse> {
  const response = await fetch(
    `${TRADING_SYSTEM_BASE}/positions/${encodeURIComponent(positionId)}/ai-decisions?limit=${limit}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    }
  );
  return handleResponse<PositionAIDecisionsResponse>(response);
}

export async function getRecommendationsTechnicalAnalysis(): Promise<RecommendationsTechnicalAnalysis> {
  const response = await fetch(`${TRADING_SYSTEM_BASE}/technical-analysis/recommendations`);
  
  if (!response.ok) {
    throw new Error(`Failed to get recommendations technical analysis: ${response.statusText}`);
  }
  
  return response.json();
}

